import { useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useNotify } from 'react-admin';

import { ACTIVE_KEY_EXISTS } from './apiKeys';

/**
 * Mint one API key and show it once.
 *
 * What the dialog does not know is who the key is for or which callable mints
 * it: the caller passes `onCreate`, which generates the key, sends only its
 * hash and returns the raw secret. An admin on a user page passes a recipient
 * and adminCreateApiKey; a user on their own Settings page passes neither and
 * createMyApiKey. Eligibility and the one-active-key rule are enforced
 * server-side; the caller only enables the button for a holder that
 * `canHoldKey` and has no active key.
 *
 * The raw key exists in this component's state and nowhere else — it is never
 * written to Firestore, only its SHA-256 is. Once the dialog closes it is
 * unrecoverable, which is why the confirmation step is deliberately awkward to
 * dismiss.
 */
export const CreateApiKeyDialog = ({
    open,
    onClose,
    onCreate,
    recipient,
    activeKeyMessage = ACTIVE_KEY_EXISTS,
}: {
    open: boolean;
    onClose: () => void;
    /** Mints the key for this dialog's owner and returns the raw secret. */
    onCreate: (label: string) => Promise<string>;
    /** Whose key it is when that is not the signed-in user: an admin on a
     *  user's page. Leave it out for a user's own key. */
    recipient?: string;
    /** What to say when the server refuses a second active key. */
    activeKeyMessage?: string;
}) => {
    const notify = useNotify();

    const [label, setLabel] = useState('');
    const [saving, setSaving] = useState(false);
    const [issued, setIssued] = useState<string | null>(null);

    const reset = () => {
        setLabel('');
        setIssued(null);
        setSaving(false);
    };

    const close = () => {
        reset();
        onClose();
    };

    const create = async () => {
        setSaving(true);
        try {
            setIssued(await onCreate(label.trim()));
        } catch (error) {
            // The caller disables Create while an active key is listed, so
            // this refusal means another session won the race, or the tab's
            // list was stale or not loaded yet. Say which rule it is rather
            // than echoing the code.
            const message =
                (error as { message?: unknown })?.message ===
                'active_key_exists'
                    ? activeKeyMessage
                    : `Could not create the key: ${String(error)}`;
            notify(message, { type: 'error' });
            setSaving(false);
        }
    };

    if (issued) {
        return (
            // Escape and backdrop clicks are refused here, and only here: this
            // is the one moment where dismissing the dialog destroys something
            // unrecoverable. The pre-creation form below stays freely
            // dismissable.
            <Dialog
                open={open}
                onClose={(_event, reason) => {
                    if (reason !== 'backdropClick') close();
                }}
                disableEscapeKeyDown
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Key created</DialogTitle>
                <DialogContent>
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        This is the only time the key is shown. It is stored as
                        a hash and cannot be recovered — if it is lost, revoke
                        it and issue another.
                    </Alert>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            p: 1.5,
                            borderRadius: 1,
                            bgcolor: 'action.hover',
                        }}
                    >
                        <Typography
                            component="code"
                            sx={{
                                fontFamily: 'monospace',
                                wordBreak: 'break-all',
                                flex: 1,
                            }}
                        >
                            {issued}
                        </Typography>
                        <Tooltip title="Copy">
                            <IconButton
                                aria-label="Copy key"
                                onClick={async () => {
                                    // Do not claim the unrecoverable secret is
                                    // safely on the clipboard until it is: this
                                    // rejects in a non-secure context or when
                                    // permission is denied.
                                    try {
                                        await navigator.clipboard.writeText(
                                            issued
                                        );
                                        notify('Key copied', { type: 'info' });
                                    } catch {
                                        notify(
                                            'Could not copy — select and copy it manually.',
                                            {
                                                type: 'warning',
                                            }
                                        );
                                    }
                                }}
                            >
                                <ContentCopyIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={close} variant="contained">
                        I have stored it
                    </Button>
                </DialogActions>
            </Dialog>
        );
    }

    return (
        // Not dismissable while the key is being created: the server mints it
        // either way, and a dialog that is gone by then never shows the secret,
        // leaving an active key nobody holds.
        <Dialog
            open={open}
            onClose={() => {
                if (!saving) close();
            }}
            maxWidth="sm"
            fullWidth
        >
            <DialogTitle>Create API key</DialogTitle>
            <DialogContent>
                {recipient && (
                    <>
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mb: 1 }}
                        >
                            For {recipient}
                        </Typography>
                        {/* The admin who creates a key for someone else is
                            the one who sees the secret, and the key acts as
                            the other person in the API. */}
                        <Alert severity="info" sx={{ mb: 1 }}>
                            This key will act as {recipient} in the API. You
                            will see its secret once, so hand it over through a
                            secure channel.
                        </Alert>
                    </>
                )}
                <TextField
                    fullWidth
                    margin="normal"
                    label="Label"
                    value={label}
                    onChange={e => setLabel(e.target.value)}
                    helperText={
                        recipient
                            ? 'Who this key is for. Shown in the key list and the audit log.'
                            : 'What this key is for. Shown in your key list and the audit log.'
                    }
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={close} disabled={saving}>
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    onClick={create}
                    disabled={!label.trim() || saving}
                >
                    Create
                </Button>
            </DialogActions>
        </Dialog>
    );
};
